import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cvData } from '@/data/cvData';
import { cvDataCompact } from '@/data/cvDataCompact';
import styles from './CVGenerator.module.css';

/**
 * Parses markdown bold syntax (**text**) and returns React elements
 */
function parseMarkdownBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

interface CVGeneratorProps {
  onClose?: () => void;
}

type CVVersion = 'compact' | 'extended';

export function CVGenerator({ onClose }: CVGeneratorProps) {
  const cvRef = useRef<HTMLDivElement>(null);
  const cvWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [version, setVersion] = useState<CVVersion>('compact');

  const generateDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Scale the CV document to fit the container width (zoom-out on small screens)
  const updateScale = useCallback(() => {
    const wrapper = cvWrapperRef.current;
    const cv = cvRef.current;
    const container = containerRef.current;
    if (!wrapper || !cv || !container) return;

    const containerWidth = container.clientWidth - 24; // account for padding
    const cvWidth = cv.scrollWidth;

    if (cvWidth > containerWidth) {
      const scale = containerWidth / cvWidth;
      wrapper.style.transform = `scale(${scale})`;
      wrapper.style.transformOrigin = 'top left';
      wrapper.style.height = `${cv.scrollHeight * scale}px`;
      wrapper.style.width = `${containerWidth}px`;
    } else {
      wrapper.style.transform = '';
      wrapper.style.height = '';
      wrapper.style.width = '';
    }
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Update scale on mount, version change, and resize
  useEffect(() => {
    // Wait a frame for the CV to render before measuring
    requestAnimationFrame(updateScale);
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [updateScale, version]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Check if we're in responsive/mobile mode (viewport width < 768px)
  const isMobileView = () => window.innerWidth < 768;

  // Static PDF paths for mobile download
  const STATIC_PDF_PATHS = {
    compact: '/Documents/Fernando_Olea_CV_2026-01-29 (1).pdf',
    extended: '/Documents/Fernando_Olea_CV_Extended_2026-01-28.pdf',
  };

  const handleDownload = async () => {
    // On mobile/responsive view, download static PDFs instead of generating
    if (isMobileView()) {
      const pdfPath = STATIC_PDF_PATHS[version];
      const filename = version === 'compact'
        ? 'Fernando_Olea_CV.pdf'
        : 'Fernando_Olea_CV_Extended.pdf';

      const link = document.createElement('a');
      link.href = pdfPath;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (!cvRef.current) return;

    setIsGenerating(true);

    try {
      const html2pdf = (await import('html2pdf.js')).default;

      // Create a full-size clone behind the modal for accurate PDF capture.
      // The visible CV may be scaled down via transform on small screens,
      // which causes html2canvas to capture a cropped/shrunk version.
      // This clone sits at z-index 9998 (behind the modal at 9999),
      // rendered at full A4 size so html2canvas gets the complete document.
      const clone = cvRef.current.cloneNode(true) as HTMLElement;
      const captureContainer = document.createElement('div');
      Object.assign(captureContainer.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '900px',
        zIndex: '9998',
        background: '#fff',
        overflow: 'visible',
      });
      captureContainer.appendChild(clone);
      document.body.appendChild(captureContainer);

      // Copy all stylesheets so CSS Module classes render correctly in the clone
      const sheets = document.querySelectorAll('style, link[rel="stylesheet"]');
      const clonedSheets: Node[] = [];
      sheets.forEach((sheet) => {
        const copy = sheet.cloneNode(true);
        captureContainer.appendChild(copy);
        clonedSheets.push(copy);
      });

      const filename = version === 'compact'
        ? `Fernando_Olea_CV_${new Date().toISOString().split('T')[0]}.pdf`
        : `Fernando_Olea_CV_Extended_${new Date().toISOString().split('T')[0]}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10] as [number, number, number, number],
        filename,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          windowWidth: 900,
        },
        jsPDF: {
          unit: 'mm' as const,
          format: 'a4' as const,
          orientation: 'portrait' as const,
        },
        pagebreak: { mode: 'avoid-all' as const, before: '.page-break' },
      };

      await html2pdf().set(opt).from(clone).save();

      document.body.removeChild(captureContainer);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const modalContent = (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>CV Preview</h2>
          <div className={styles.modalActions}>
            <div className={styles.versionToggle}>
              <button
                className={`${styles.versionBtn} ${version === 'compact' ? styles.versionBtnActive : ''}`}
                onClick={() => setVersion('compact')}
              >
                ATS (1-2 pág)
              </button>
              <button
                className={`${styles.versionBtn} ${version === 'extended' ? styles.versionBtnActive : ''}`}
                onClick={() => setVersion('extended')}
              >
                Extended
              </button>
            </div>
            <button
              className={styles.downloadBtn}
              onClick={handleDownload}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Download PDF'}
            </button>
            {onClose && (
              <button className={styles.closeBtn} onClick={onClose}>
                Close
              </button>
            )}
          </div>
        </div>

        <div className={styles.cvContainer} ref={containerRef}>
          <div ref={cvWrapperRef} className={styles.cvWrapper}>
            {version === 'compact' ? (
              <CompactCV ref={cvRef} generateDate={generateDate} />
            ) : (
              <ExtendedCV ref={cvRef} generateDate={generateDate} />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render outside the Header's stacking context
  return createPortal(modalContent, document.body);
}

// Compact CV Component (ATS-optimized, 1-2 pages)
import { forwardRef } from 'react';

interface CVContentProps {
  generateDate: string;
}

const CompactCV = forwardRef<HTMLDivElement, CVContentProps>(({ generateDate }, ref) => {
  const data = cvDataCompact;

  return (
    <div ref={ref} className={styles.cv}>
      {/* Header - Contact Information */}
      <header className={styles.header}>
        <h1 className={styles.name}>{data.personal.name}</h1>
        <p className={styles.title}>{data.personal.title}</p>
        <div className={styles.contactInfo}>
          <a href={`mailto:${data.personal.email}`}>{data.personal.email}</a>
          <span className={styles.separator}>|</span>
          <a href={`tel:${data.personal.phone.replace(/\s/g, '')}`}>{data.personal.phone}</a>
          <span className={styles.separator}>|</span>
          <a href={`https://${data.personal.website}`} target="_blank" rel="noopener noreferrer">{data.personal.website}</a>
          <span className={styles.separator}>|</span>
          <a href={`https://${data.personal.linkedin}`} target="_blank" rel="noopener noreferrer">{data.personal.linkedin}</a>
        </div>
        <div className={styles.contactInfo}>
          <span>{data.personal.location}</span>
          <span className={styles.separator}>|</span>
          <span>{data.personal.languages}</span>
        </div>
      </header>

      {/* Professional Summary */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Professional Summary</h2>
        <p className={styles.summary}>{parseMarkdownBold(data.summary)}</p>
      </section>

      {/* Technical Skills */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Skills</h2>
        <div className={styles.skillsGrid}>
          {data.skills.map((skillGroup, i) => (
            <div key={i} className={styles.skillCategory}>
              <h3 className={styles.skillCategoryTitle}>{skillGroup.category}</h3>
              <p className={styles.skillItems}>{skillGroup.items.join(' · ')}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Professional Experience */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Professional Experience</h2>
        {data.experience.map((exp, i) => (
          <div key={i} className={styles.experienceItem}>
            <div className={styles.experienceHeader}>
              <div>
                <h3 className={styles.role}>{exp.role}</h3>
                <p className={styles.company}>
                  {exp.company} - {exp.location}
                </p>
              </div>
              <span className={styles.dates}>{exp.period}</span>
            </div>
            <ul className={styles.achievements}>
              {exp.achievements.map((achievement, j) => (
                <li key={j}>{parseMarkdownBold(achievement)}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Education */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Education</h2>
        {data.education.map((edu, i) => (
          <div key={i} className={styles.educationItem}>
            <div className={styles.educationHeader}>
              <div>
                <h3 className={styles.degree}>{edu.degree}</h3>
                <p className={styles.institution}>{edu.institution}</p>
              </div>
              <span className={styles.year}>{edu.year}</span>
            </div>
          </div>
        ))}
      </section>

      {/* Footer with generation date */}
      <footer className={styles.footer}>
        <p>
          CV generated on {generateDate} from{' '}
          <a href="https://fernandoolea.com">fernandoolea.com</a>
        </p>
      </footer>
    </div>
  );
});

CompactCV.displayName = 'CompactCV';

// Extended CV Component (Full details)
const ExtendedCV = forwardRef<HTMLDivElement, CVContentProps>(({ generateDate }, ref) => {
  return (
    <div ref={ref} className={styles.cv}>
      {/* Header - Contact Information */}
      <header className={styles.header}>
        <h1 className={styles.name}>{cvData.personal.name}</h1>
        <p className={styles.title}>{cvData.personal.title}</p>
        <div className={styles.contactInfo}>
          <a href={`mailto:${cvData.personal.email}`}>{cvData.personal.email}</a>
          <span className={styles.separator}>|</span>
          {cvData.personal.phone && (
            <>
              <a href={`tel:${cvData.personal.phone.replace(/\s/g, '')}`}>{cvData.personal.phone}</a>
              <span className={styles.separator}>|</span>
            </>
          )}
          <a href={`https://${cvData.personal.website}`} target="_blank" rel="noopener noreferrer">{cvData.personal.website}</a>
          <span className={styles.separator}>|</span>
          <a href={`https://${cvData.personal.linkedin}`} target="_blank" rel="noopener noreferrer">{cvData.personal.linkedin}</a>
        </div>
        <div className={styles.contactInfo}>
          <span>{cvData.personal.location}</span>
          <span className={styles.separator}>|</span>
          {cvData.personal.languages.map((lang, i) => (
            <span key={i}>
              {lang.language}: {lang.level}
              {i < cvData.personal.languages.length - 1 && ' | '}
            </span>
          ))}
        </div>
      </header>

      {/* Professional Summary */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Professional Summary</h2>
        <p className={styles.summary}>{parseMarkdownBold(cvData.summary)}</p>
      </section>

      {/* Leadership & Soft Skills */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Leadership & Soft Skills</h2>
        <div className={styles.skillsGrid}>
          {cvData.skills
            .filter((s) => s.category === 'Leadership & Management' || s.category === 'Soft Skills')
            .map((skillGroup, i) => (
              <div key={i} className={styles.skillCategory}>
                <h3 className={styles.skillCategoryTitle}>{skillGroup.category}</h3>
                <p className={styles.skillItems}>{skillGroup.items.join(', ')}</p>
              </div>
            ))}
        </div>
      </section>

      {/* Technical Skills */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Technical Skills</h2>
        <div className={styles.skillsGrid}>
          {cvData.skills
            .filter((s) => s.category !== 'Leadership & Management' && s.category !== 'Soft Skills')
            .map((skillGroup, i) => (
              <div key={i} className={styles.skillCategory}>
                <h3 className={styles.skillCategoryTitle}>{skillGroup.category}</h3>
                <p className={styles.skillItems}>{skillGroup.items.join(', ')}</p>
              </div>
            ))}
        </div>
      </section>

      {/* Professional Experience */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Professional Experience</h2>
        {cvData.experience.map((exp, i) => (
          <div key={i} className={styles.experienceItem}>
            <div className={styles.experienceHeader}>
              <div>
                <h3 className={styles.role}>{exp.role}</h3>
                <p className={styles.company}>
                  {exp.company} - {exp.location}
                </p>
              </div>
              <span className={styles.dates}>
                {exp.startDate} - {exp.endDate}
              </span>
            </div>
            <ul className={styles.achievements}>
              {exp.achievements.map((achievement, j) => (
                <li key={j}>{parseMarkdownBold(achievement)}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* Key Projects */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Key Projects</h2>
        <div className={styles.projectsGrid}>
          {cvData.keyProjects.map((project, i) => (
            <div key={i} className={styles.projectItem}>
              <h3 className={styles.projectName}>{project.name}</h3>
              <p className={styles.projectDesc}>{project.description}</p>
              <p className={styles.projectImpact}>
                <strong>Impact:</strong> {project.impact}
              </p>
              <p className={styles.projectTech}>
                <strong>Technologies:</strong> {project.technologies.join(', ')}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Education */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Education</h2>
        {cvData.education.map((edu, i) => (
          <div key={i} className={styles.educationItem}>
            <div className={styles.educationHeader}>
              <div>
                <h3 className={styles.degree}>{edu.degree}</h3>
                <p className={styles.institution}>{edu.institution}</p>
                {edu.description && (
                  <p className={styles.eduDescription}>{edu.description}</p>
                )}
              </div>
              <span className={styles.year}>{edu.year}</span>
            </div>
          </div>
        ))}
      </section>

      {/* Footer with generation date */}
      <footer className={styles.footer}>
        <p>
          CV generated on {generateDate} from{' '}
          <a href="https://fernandoolea.com">fernandoolea.com</a>
        </p>
      </footer>
    </div>
  );
});

ExtendedCV.displayName = 'ExtendedCV';
